// Shared effects for the 9INE theme: the Motion setting, Locomotive-style text decoding, pixel effects for photos and
// panels, and small animation helpers. Ported from the approved concept (concept/src/06-core.js).

export const $ = (s, r = document) => r.querySelector(s);
export const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
export const root = document.documentElement;
export const wait = (ms) => new Promise((res) => setTimeout(res, ms));
export const store = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* storage blocked */ } }
};
export const session = {
  get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* storage blocked */ } },
  remove(k) { try { sessionStorage.removeItem(k); } catch (e) { /* storage blocked */ } }
};
export const editor = !!(window.Shopify && window.Shopify.designMode);

const shuffled = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [a[i], a[j]] = [a[j], a[i]]; }
  return a;
};

/* ---------- motion ---------- */
// On for everyone until the visitor switches Motion off on the start page ('9ine-motion'); the device's
// reduce-motion setting isn't followed (the owner's call, 8 Oct 2026). The boot script in <head> has already set the
// `still` class, so this starts from it.
export let motion = !root.classList.contains('still');
export function setMotion(on) {
  motion = on;
  store.set('9ine-motion', on ? 'on' : 'off');
  root.classList.toggle('still', !on);
  document.dispatchEvent(new CustomEvent('motion:change', { detail: { on } }));
}

/* ---------- decode engine (Locomotive-style) ---------- */
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%&*+=/<>[]{}?!';
const glyph = () => GLYPHS[(Math.random() * GLYPHS.length) | 0];
const jobs = new Set();
let frame = 0;

function tick(now) {
  for (const job of jobs) if (job(now)) jobs.delete(job);
  frame = jobs.size ? requestAnimationFrame(tick) : 0;
}
function start(el, job) {
  if (el._job) jobs.delete(el._job);
  el._job = job;
  jobs.add(job);
  if (!frame) frame = requestAnimationFrame(tick);
}
function stop(el) {
  if (el._job) { jobs.delete(el._job); el._job = null; }
}

// Wrap every character in its own span so it can flicker without moving the layout.
export function split(el) {
  if (el._chars) return el._chars;
  const text = el.textContent.replace(/\s+/g, ' ').trim();
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) {
    const n = walker.currentNode;
    if (n.textContent.trim() && !n.parentElement.closest('.sr-only')) nodes.push(n);
  }
  const chars = [];
  let i = 0;
  for (const node of nodes) {
    const wrapEl = document.createElement('span');
    wrapEl.setAttribute('aria-hidden', 'true');
    for (const ch of node.textContent) {
      if (/\s/.test(ch)) { wrapEl.append(ch); if (chars.length) i++; continue; }
      const c = document.createElement('span');
      c.className = 'c';
      c.textContent = ch;
      c._i = i++;
      c._m = 0;
      wrapEl.append(c);
      chars.push(c);
    }
    node.replaceWith(wrapEl);
  }
  if (el.matches('a, button')) {
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', text);
  } else if (!el.closest('[aria-hidden="true"]')) {
    const sr = document.createElement('span');
    sr.className = 'sr-only';
    sr.textContent = text;
    el.prepend(sr);
  }
  // From here the letters hide and show themselves, so the element itself (and its screen-reader copy) stays visible.
  el.classList.add('is-split');
  el._chars = chars;
  return chars;
}

export function setText(el, text) {
  stop(el);
  el._chars = null;
  if (el.matches('a, button')) el.removeAttribute('aria-label');
  el.textContent = text;
}

// mode 0 = final character, 1 = flickering glyph, 2 = hidden
function paint(c, mode, now) {
  if (mode === 1) {
    if (c._m !== 1) { c.className = 'c scr'; c._m = 1; c._t = 0; }
    if (now - c._t > 55) { c.dataset.g = glyph(); c._t = now; }
  } else if (c._m !== mode) {
    c.className = mode === 2 ? 'c off' : 'c';
    c._m = mode;
  }
}

export function decode(el, o = {}) {
  const chars = split(el);
  if (!motion) { stop(el); chars.forEach((c) => paint(c, 0)); if (o.done) o.done(); return; }
  const { delay = 0, step = 26, hold = 150 } = o;
  const t0 = performance.now() + delay;
  chars.forEach((c) => paint(c, 2));
  start(el, (now) => {
    let busy = false;
    for (const c of chars) {
      const t = t0 + c._i * step;
      if (now < t) { paint(c, 2); busy = true; }
      else if (now < t + hold) { paint(c, 1, now); busy = true; }
      else paint(c, 0);
    }
    if (!busy) { el._job = null; if (o.done) o.done(); }
    return !busy;
  });
}

export function scramble(el, o = {}) {
  const chars = split(el);
  if (!motion || el._job) return;
  const { step = 22, hold = 240 } = o;
  const t0 = performance.now();
  start(el, (now) => {
    let busy = false;
    for (const c of chars) {
      if (now < t0 + c._i * step + hold) { paint(c, 1, now); busy = true; }
      else paint(c, 0);
    }
    if (!busy) el._job = null;
    return !busy;
  });
}

/* ---------- pixel effects ---------- */
export const PIX_STEPS = [8, 16, 32, 48, 96, 128];
export const PIX_SWAP = [8, 16, 32, 64, 128];

// Locomotive-style load-in: the photo is drawn 8 → 128 blocks across on a canvas over the <img>, then removed.
export function depixelate(img, steps = PIX_STEPS, dt = 100) {
  if (!motion || !img) return;
  const box = img.parentElement;
  const token = (img._pix = (img._pix || 0) + 1);
  const run = async () => {
    if (img._pix !== token || !img.naturalWidth) return;
    const old = box.querySelector(':scope > canvas.pix');
    if (old) old.remove();
    const canvas = document.createElement('canvas');
    canvas.className = 'pix';
    canvas.setAttribute('aria-hidden', 'true');
    const W = 128;
    const H = Math.max(1, Math.round((W * img.naturalHeight) / img.naturalWidth));
    canvas.width = W;
    canvas.height = H;
    const cs = getComputedStyle(img);
    canvas.style.objectFit = cs.objectFit;
    canvas.style.objectPosition = cs.objectPosition;
    box.append(canvas);
    const ctx = canvas.getContext('2d');
    const small = document.createElement('canvas');
    const sctx = small.getContext('2d');
    try {
      for (const n of steps) {
        if (!canvas.isConnected || img._pix !== token) { canvas.remove(); return; }
        small.width = n;
        small.height = Math.max(1, Math.round((n * H) / W));
        sctx.drawImage(img, 0, 0, small.width, small.height);
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(small, 0, 0, W, H);
        await wait(dt);
      }
    } catch (e) { /* a cross-origin image without CORS can't be drawn: just show it */ }
    canvas.remove();
  };
  if (img.complete && img.naturalWidth) run();
  else img.addEventListener('load', run, { once: true });
}

// Swap a photo through the pixel effect: decode the new file first so nothing flashes.
export async function swapPhoto(img, src, srcset, alt) {
  const pre = new Image();
  if (srcset) { pre.srcset = srcset; pre.sizes = img.sizes; }
  pre.src = src;
  try { await Promise.race([pre.decode(), wait(1200)]); } catch (e) { /* show it anyway */ }
  if (srcset) img.srcset = srcset; else img.removeAttribute('srcset');
  img.src = src;
  if (alt !== undefined) img.alt = alt;
  depixelate(img, PIX_SWAP, 70);
}

// The reverse of depixelate, for an inline SVG drawn with <use href="#symbol"> (the loading screen's crest): it breaks
// into bigger and bigger blocks, the last blocks drop out at random, and it's gone. Resolves when it has gone.
export async function pixelateAway(svg, { steps = [96, 48, 32, 16, 8], dt = 80 } = {}) {
  if (!svg) return;
  const hide = () => { svg.style.visibility = 'hidden'; };
  const use = svg.querySelector('use');
  const ref = use && document.querySelector(use.getAttribute('href'));
  const box = svg.getBoundingClientRect();
  if (!motion || !ref || !box.width) { hide(); return; }
  // A standalone image of the crest, coloured explicitly (an <img> can't inherit currentColor).
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const W = Math.round(box.width * dpr);
  const H = Math.round(box.height * dpr);
  const color = getComputedStyle(svg).color;
  const markup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${svg.getAttribute('viewBox')}" width="${W}" height="${H}" style="color:${color}">${ref.innerHTML}</svg>`;
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  try { await Promise.race([img.decode(), wait(400)]); } catch (e) { hide(); return; }
  const canvas = document.createElement('canvas');
  canvas.className = 'pix-out';
  canvas.setAttribute('aria-hidden', 'true');
  canvas.width = W;
  canvas.height = H;
  canvas.style.width = `${box.width}px`;
  canvas.style.height = `${box.height}px`;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, W, H);
  svg.before(canvas);
  svg.style.display = 'none';
  const small = document.createElement('canvas');
  const sctx = small.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  for (const n of steps) {
    await wait(dt);
    small.width = n;
    small.height = Math.max(1, Math.round((n * H) / W));
    sctx.drawImage(img, 0, 0, small.width, small.height);
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(small, 0, 0, W, H);
  }
  // The coarsest blocks drop out in four goes.
  const cols = small.width;
  const rows = small.height;
  const order = shuffled(cols * rows);
  const cw = W / cols;
  const ch = H / rows;
  for (let k = 0; k < 4; k++) {
    await wait(dt);
    for (let i = Math.floor((order.length * k) / 4); i < Math.floor((order.length * (k + 1)) / 4); i++) {
      const c = order[i];
      ctx.clearRect(Math.floor((c % cols) * cw), Math.floor(((c / cols) | 0) * ch), Math.ceil(cw) + 1, Math.ceil(ch) + 1);
    }
  }
  canvas.remove();
}

// Pixel build for panels: a wall of blocks that clears from left to right in 60 ms steps.
export function dissolve(panel) {
  if (!motion) return;
  let cv = panel.querySelector(':scope > canvas.dissolve');
  if (!cv) {
    cv = document.createElement('canvas');
    cv.className = 'dissolve';
    cv.setAttribute('aria-hidden', 'true');
    panel.append(cv);
  }
  const B = panel.clientWidth < 420 ? 18 : 22;
  const cols = Math.max(1, Math.ceil(panel.clientWidth / B));
  const rows = Math.max(1, Math.ceil(panel.clientHeight / B));
  cv.width = cols;
  cv.height = rows;
  const ctx = cv.getContext('2d');
  const color = getComputedStyle(root).getPropertyValue('--fg').trim() || '#F2F2F2';
  const cells = new Float32Array(cols * rows);
  for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++) cells[y * cols + x] = 0.08 + (x / cols) * 0.5 + Math.random() * 0.42;
  const id = (cv._run = (cv._run || 0) + 1);
  const t0 = performance.now();
  const D = 660;
  cv.hidden = false;
  let lastStep = -1;
  const draw = () => {
    if (cv._run !== id) return;
    const k = Math.floor((performance.now() - t0) / 60);
    if (k !== lastStep) {
      lastStep = k;
      const t = (k * 60) / D;
      ctx.clearRect(0, 0, cols, rows);
      if (t >= 1) { cv.hidden = true; return; }
      ctx.fillStyle = color;
      for (let i = 0; i < cells.length; i++) if (cells[i] > t) ctx.fillRect(i % cols, (i / cols) | 0, 1, 1);
    }
    setTimeout(draw, 16);
  };
  draw();
}

// Full-screen pixel wipe for the theme switch: blocks of the new colour fill in, the theme swaps, the blocks clear.
export function pixelWipe(color, mid) {
  if (!motion) { mid(); return Promise.resolve(); }
  const cv = document.createElement('canvas');
  cv.className = 'wipe';
  cv.setAttribute('aria-hidden', 'true');
  const B = window.innerWidth < 700 ? 30 : 40;
  const cols = Math.ceil(window.innerWidth / B);
  const rows = Math.ceil(window.innerHeight / B);
  cv.width = cols;
  cv.height = rows;
  document.body.append(cv);
  const ctx = cv.getContext('2d');
  ctx.fillStyle = color;
  const N = cols * rows;
  const fillOrder = shuffled(N);
  const clearOrder = shuffled(N);
  const STEPS = 6;
  return new Promise((res) => {
    let k = 0;
    const put = (order, fn) => {
      const from = Math.round((N * k) / STEPS);
      k++;
      const to = Math.round((N * k) / STEPS);
      for (let i = from; i < to; i++) { const c = order[i]; fn(c % cols, (c / cols) | 0); }
    };
    const fill = () => {
      put(fillOrder, (x, y) => ctx.fillRect(x, y, 1, 1));
      if (k < STEPS) { setTimeout(fill, 45); return; }
      mid();
      k = 0;
      setTimeout(clear, 90);
    };
    const clear = () => {
      put(clearOrder, (x, y) => ctx.clearRect(x, y, 1, 1));
      if (k < STEPS) { setTimeout(clear, 45); return; }
      cv.remove();
      res();
    };
    fill();
  });
}

/* ---------- tween + caret helpers ---------- */
export const EASE = {
  inQuad: (t) => t * t,
  outQuad: (t) => 1 - (1 - t) * (1 - t)
};
export function tween(ms, onUpdate, ease = EASE.outQuad) {
  return new Promise((res) => {
    const t0 = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - t0) / ms);
      onUpdate(ease(p));
      if (p < 1) requestAnimationFrame(step); else res();
    };
    requestAnimationFrame(step);
  });
}

// Run once the page has made its entrance: theme.js adds `page-in` to <html> and fires page:in when the loading
// screen or the iris has gone. Section scripts start their load-in effects from here.
export function onPageIn(fn) {
  if (root.classList.contains('page-in')) fn();
  else document.addEventListener('page:in', () => fn(), { once: true });
}

// Move a small triangle marker next to (or below) a menu item.
export function place(caret, target, instant, below) {
  if (!caret) return;
  caret.style.opacity = target ? '1' : '0';
  if (!target) return;
  if (instant) caret.style.transition = 'none';
  const x = below ? target.offsetLeft + target.offsetWidth / 2 - 6 : target.offsetLeft;
  const y = below ? 0 : target.offsetTop + target.offsetHeight / 2 - 4;
  caret.style.transform = `translate(${x}px, ${y}px)`;
  if (instant) { void caret.offsetWidth; caret.style.transition = ''; }
}

// Shopify's bot check (hCaptcha) takes over the contact and sign-up forms once someone clicks into one, and sends the
// form itself after its check, past any check on the form. So a form's own check runs first, at the window: a click
// on its button (Enter in a field clicks it too) or its send stops there when `check()` says no, and Shopify's script
// never sees it. `check` shows its own messages and returns true when the form may go.
const firstChecks = new WeakMap();
let firstBound = false;
export function checkFirst(form, check) {
  firstChecks.set(form, check);
  if (firstBound) return;
  firstBound = true;
  const stop = (e) => {
    const f = e.type === 'submit' ? e.target : e.target.closest?.('button[type="submit"], input[type="submit"]')?.form;
    const ok = f && firstChecks.get(f);
    if (!ok || ok()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  };
  window.addEventListener('click', stop, true);
  window.addEventListener('submit', stop, true);
}
